import { gql } from '@apollo/client';
import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
} from '@chakra-ui/react';
import { useCallback } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { SiteItemFragment } from './__generated__/SitesActionTable.graphql';

interface Props {
  site: SiteItemFragment | null;
  isOpen: boolean;
  onCancel: () => void;
  onDelete?: (site: SiteItemFragment | null) => void;
}

export function DeleteSiteModal({ site, isOpen, onCancel, onDelete }: Props) {
  const { t } = useTranslation();
  const isSiteUsedByConfigurations = site?.isSiteUsedByAnyConfiguration;

  const handleClose = useCallback(() => {
    onCancel();
  }, [onCancel]);

  const handleDelete = useCallback(() => {
    if (!onDelete || !site) return;
    onDelete(site);
  }, [onDelete, site]);

  return (
    <Modal isOpen={isOpen} onClose={onCancel}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {isSiteUsedByConfigurations ? t('siteDeleteError') : t('removeSite')}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text>
            {isSiteUsedByConfigurations ? (
              t('siteDeleteConfigError')
            ) : (
              <Trans
                i18nKey="deleteCustomerConfirm"
                values={{ name: site?.name }}
                components={{ strong: <strong /> }}
              />
            )}
          </Text>
        </ModalBody>
        <ModalFooter>
          {isSiteUsedByConfigurations ? (
            <Button onClick={handleClose} colorScheme="blue">
              {t('ok')}
            </Button>
          ) : (
            <>
              <Button onClick={handleClose} variant="ghost" colorScheme="blue">
                {t('cancel')}
              </Button>
              <Button onClick={handleDelete} colorScheme="red" ml={3}>
                {t('delete')}
              </Button>
            </>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

DeleteSiteModal.graphql = {
  fragments: {
    SiteItem: gql`
      fragment SiteItem on Site {
        id
        name
        address
        isSiteUsedByAnyConfiguration
        ...EditSiteModal
      }
    `,
  },
  removeSite: gql`
    mutation RemoveSite($input: SiteDeleteInput!) {
      siteDelete(input: $input) {
        siteId
      }
    }
  `,
};
